import { Component } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule, ButtonOptions } from '@usitsdasdesign/dds-ng/button';
import { DropdownItemOptions, DropdownModule, DropdownOptions } from '@usitsdasdesign/dds-ng/dropdown';
import { TabsModule } from '@usitsdasdesign/dds-ng/tabs';
import { ServicesService } from '../../core/services/services.service';
import { ExtThemes, ButtonKind, Size, WidthState, ErrorState, LabelPosition } from '@usitsdasdesign/dds-ng/shared';
import { Specification } from '../../core/models/specification.models';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DescriptionType, InputModule, InputOptions, TextAlign } from '@usitsdasdesign/dds-ng/input';
import { TextareaModule, TextareaOptions } from '@usitsdasdesign/dds-ng/textarea';
import { SpecificationsService } from '../../core/services/specifications.service';
import { getSpecificationLabel } from '../../core/utils/label.utils';

@Component({
  selector: 'app-create-service',
  standalone: true,
  imports: [ButtonModule, TabsModule, DropdownModule, RouterModule, InputModule, TextareaModule, ReactiveFormsModule],
  templateUrl: './create-service.component.html',
  styleUrl: './create-service.component.scss',
})
export class CreateServiceComponent {
  projectId: string;
  serviceTypeId: string;
  specificationId: string;
  specifications: Specification[];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private servicesService: ServicesService,
    private specificationsService: SpecificationsService,
  ) {}

  serviceTypeDropdownOptions: DropdownOptions = {
    label: 'API Simulation',
    ariaLabel: 'service type dropdown',
    theme: ExtThemes.dark,
    kind: ButtonKind.primary,
    size: Size.md,
    width: WidthState.fixed,
    isColorBg: false,
    isInverse: false,
    customClass: '',
    disabled: false,
    isResponsive: false,
    stickerIsOutsideClick: false,
    stickerShift: 0,
    stickerMaxHeight: '',
    stickerIsDisabled: false,
    stickerPosition: 'bottom-left',
    stickerIndent: 0,
    stickerCustomClass: '',
    stickerIsDynamic: true,
  };

  serviceTypeDropdownItems: DropdownItemOptions[];

  serviceTypeSelected(value: string) {
    this.serviceTypeId = value;
  }

  specificationDropdownOptions: DropdownOptions = {
    label: '',
    ariaLabel: 'api specification dropdown',
    theme: ExtThemes.dark,
    kind: ButtonKind.primary,
    size: Size.md,
    width: WidthState.fixed,
    isColorBg: false,
    isInverse: false,
    customClass: '',
    disabled: false,
    isResponsive: false,
    stickerIsOutsideClick: false,
    stickerShift: 0,
    stickerMaxHeight: '',
    stickerIsDisabled: false,
    stickerPosition: 'bottom-left',
    stickerIndent: 0,
    stickerCustomClass: '',
    stickerIsDynamic: true,
  };

  specificationDropdownItems: DropdownItemOptions[];

  specificationSelected(value: string) {
    this.setSpecificationContent(value);
  }

  inputFormCtrl = new FormControl({ value: '', disabled: false });

  inputOptions: InputOptions = {
    label: '',
    labelPosition: LabelPosition.external,
    placeholder: '',
    isError: false,
    isRequired: true,
    isInverse: false,
    size: Size.md,
    description: '',
    descriptionType: DescriptionType.text,
    errorMessage: '',
    errorState: ErrorState.default,
    textAlign: TextAlign.left,
    type: 'text',
  };

  changeValue() {}

  onBlur() {}

  textareaFormCtrl = new FormControl({ value: '', disabled: false });

  textareaOptions: TextareaOptions = {
    label: '',
    labelPosition: LabelPosition.external,
    placeholder: '',
    minHeight: 250,
    isRequired: false,
    isInverse: false,
    readonly: false,
    isError: true,
    errorState: ErrorState.default,
    description: '',
    errorMessage: '',
    size: Size.md,
  };

  descriptionValueChanged() {
    console.log('descriptionValueChanged', this.textareaFormCtrl.value);
  }

  addServiceButtonOptions: ButtonOptions = {
    theme: ExtThemes.green,
    kind: ButtonKind.primaryLoud,
    size: Size.md,
    width: WidthState.fixed,
    isLoading: false,
    icon: 'dds-icon_plus',
    isIconLeft: false,
    isColorBg: false,
    isInverse: false,
    disabled: false,
    ariaLabel: 'Add Service',
    customClass: '',
    role: 'button',
  };

  addServiceClick(): void {
    // TODO: add logic for service type
    this.servicesService
      .createService(this.projectId, {
        name: this.inputFormCtrl.value ?? '',
        description: this.textareaFormCtrl.value ?? '',
        type: 'standard',
        project: this.projectId,
        specification: this.specificationId,
        model: 'cmcb2d8x600039u1xb8d4guwq', // TODO: allow model selection
      })
      .subscribe((e) => {
        console.log(e);
        this.router.navigate(['../services'], { relativeTo: this.route });
      });
  }

  cancelButtonOptions: ButtonOptions = {
    theme: ExtThemes.green,
    kind: ButtonKind.primary,
    size: Size.md,
    width: WidthState.fixed,
    isLoading: false,
    icon: '',
    isIconLeft: true,
    isColorBg: false,
    isInverse: false,
    disabled: false,
    ariaLabel: 'Cancel',
    customClass: '',
    role: 'button',
  };

  cancelClick(event: Event): void {
    this.router.navigate(['../services'], { relativeTo: this.route });
  }

  ngOnInit(): void {
    this.serviceTypeDropdownItems = [];
    this.specificationDropdownItems = [];
    this.specifications = [];

    this.route.parent?.paramMap.subscribe((params) => {
      this.projectId = params.get('id') ?? '';

      this.specificationsService.getSpecificationsForProject(this.projectId).subscribe((response) => {
        this.specifications = response == null ? [] : response;
        this.populateDropdown();
      });
    });
  }

  private populateDropdown(): void {
    if (this.specifications.length > 0) {
      this.specificationDropdownOptions.label = getSpecificationLabel(this.specifications[0]);
      this.specificationId = this.specifications[0].id; // Initialize with first specification

      for (let specification of this.specifications) {
        this.specificationDropdownItems.push({
          heading: getSpecificationLabel(specification),
          value: specification.id,
        });
      }
    }
  }

  private setSpecificationContent(id: string) {
    let specification = this.specifications.find((x) => x.id == id);
    if (specification == undefined) return;
    this.specificationId = id;
    this.specificationDropdownOptions.label = getSpecificationLabel(specification);
  }
}
